import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useMinimizableModal } from '../use-minimizable-modal/index.tsx';
import { TransitionState } from '../use-minimizable-modal/types.tsx';

export const useEnterFocusState = () => {
	const {
		view,
		setTransitionByKeyboardFrom,
		transitionByKeyboardFrom,
		isTransitionInState,
		shouldFocusChanger,
		setShouldFocusChanger,
	} = useMinimizableModal();

	// Keep refs in this order in order not to affect the unit tests
	const minimizedButtonRef = useRef<HTMLButtonElement>(null);
	const modalButtonRef = useRef<HTMLButtonElement>(null);
	const miniButtonRef = useRef<HTMLButtonElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [init, setInit] = useState(false);

	const isViewEntered = isTransitionInState([view], TransitionState.ENTERED);

	const onKeyDown = useCallback(
		(ev: KeyboardEvent<HTMLButtonElement>) => {
			if (!isViewEntered) return;
			// 13: Enter Key; 32: Space Key
			setTransitionByKeyboardFrom([13, 32].includes(ev.keyCode) ? view : null);
		},
		[setTransitionByKeyboardFrom, isViewEntered, view],
	);

	useEffect(() => {
		if (!init && isViewEntered && shouldFocusChanger) {
			setInit(true);
			setShouldFocusChanger(false);

			const buttonRef = {
				modal: modalButtonRef.current,
				mini: miniButtonRef.current,
				minimized: minimizedButtonRef.current,
				fullscreen: null,
			};

			requestAnimationFrame(() => {
				const buttonToFocus =
					transitionByKeyboardFrom !== null && view === 'minimized'
						? 'minimized'
						: transitionByKeyboardFrom;

				if (buttonToFocus && buttonRef[buttonToFocus]) {
					// @ts-expect-error - TS2571 - Object is of type 'unknown'.
					buttonRef[buttonToFocus].focus();
					setTransitionByKeyboardFrom(null);
				} else if (wrapperRef.current) {
					wrapperRef.current.focus();
				}
			});
		}
	}, [
		transitionByKeyboardFrom,
		setTransitionByKeyboardFrom,
		init,
		view,
		isViewEntered,
		shouldFocusChanger,
		setShouldFocusChanger,
	]);

	return {
		onKeyDown,
		minimizedButtonRef,
		modalButtonRef,
		miniButtonRef,
		wrapperRef,
	};
};
